/**
 * NotificationsProvider (P-18)
 *
 * Lifts the notifications subscription out of AppHeader/NotificationBell so
 * the Realtime channel is established once at app boot and survives page
 * navigation.  Without this, every route change that unmounts/remounts
 * AppHeader tears down and recreates the channel.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";

export interface AppNotification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string;
  link: string;
  read_at: string | null;
  created_at: string;
}

interface NotificationsContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotification: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const isMounted = useRef(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const subscriptionVersion = useRef(0);

  // StrictMode-safe: reset to true on mount, so the second (real) mount can
  // setNotifications. Otherwise the first mount's cleanup leaves
  // isMounted.current = false and the realtime data never reaches state.
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    const user = await getSessionUser();
    if (!user || !isMounted.current) return;

    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20);

    if (!isMounted.current) return;
    const items = (data || []) as AppNotification[];
    setNotifications(items);
    setUnreadCount(items.filter((n) => !n.read_at).length);
  }, []);

  useEffect(() => {
    const version = ++subscriptionVersion.current;

    async function setupAndSubscribe() {
      const user = await getSessionUser();
      if (!user || !isMounted.current || version !== subscriptionVersion.current) return;

      await load();

      if (!isMounted.current || version !== subscriptionVersion.current) return;

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      // Realtime subscription — filter at the Postgres level so only this
      // user's notifications are pushed (prevents all CCU receiving all events)
      const nextChannel = supabase
        .channel("user-notifications-provider")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const newNotif = payload.new as AppNotification;
            setNotifications((prev) => [newNotif, ...prev].slice(0, 20));
            setUnreadCount((c) => c + 1);
          }
        )
        .subscribe();

      if (!isMounted.current || version !== subscriptionVersion.current) {
        supabase.removeChannel(nextChannel);
        return;
      }

      channelRef.current = nextChannel;
    }

    setupAndSubscribe();

    return () => {
      subscriptionVersion.current += 1;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [load]);

  const markAsRead = useCallback(async (id: string) => {
    await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    const user = await getSessionUser();
    if (!user) return;
    await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .is("read_at", null);
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
    );
    setUnreadCount(0);
  }, []);

  const clearNotification = useCallback(async (id: string) => {
    await supabase.from("notifications").delete().eq("id", id);
    setNotifications((prev) => {
      const wasUnread = prev.find((n) => n.id === id && !n.read_at);
      if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  return (
    <NotificationsContext.Provider
      value={{ notifications, unreadCount, markAsRead, markAllAsRead, clearNotification, refresh: load }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

/** Consume the provider's notification state. Must be a descendant of NotificationsProvider. */
export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotificationsContext must be used within NotificationsProvider");
  return ctx;
}
